
import React from "react";
import {
  Type,
  Hash,
  Calendar,
  Table2,
  Braces,
} from "lucide-react";
import { API_BASE, UNIVERSAL_EXTRACT_PATH } from "./extractorApi";

export const Spinner = ({ size = 16 }) => (
  <svg
    className="animate-spin"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
  >
    <path d="M21 12a9 9 0 1 1-6.219-8.56" />
  </svg>
);

// ─── ID generator ────────────────────────────────────────────────────────────

let _docIdCounter = 0;
export const nextDocId = () => `doc_${++_docIdCounter}_${Date.now()}`;

// ─── Helpers ─────────────────────────────────────────────────────────────────

export const generateSlug = (name) =>
  name
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");

export const formatBytes = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

export const copyTextToClipboard = async (text) => {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "");
  textarea.style.position = "absolute";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand("copy");
  document.body.removeChild(textarea);
};

// ─── Page range helpers ──────────────────────────────────────────────────────

export const validatePageRange = (startPage, endPage) => {
  if (startPage == null && endPage == null) return null;
  if (startPage == null || endPage == null) {
    return "Set both start and end page, or leave both empty for the full document.";
  }
  if (!Number.isInteger(startPage) || startPage < 1) {
    return "Start page must be 1 or greater.";
  }
  if (!Number.isInteger(endPage) || endPage < startPage) {
    return "End page must be greater than or equal to start page.";
  }
  return null;
};

export const describePageRange = (startPage, endPage) =>
  startPage != null && endPage != null
    ? `Pages ${startPage}–${endPage}`
    : "Full Document";

// ─── Shared template validation (used by the Save button AND the JSON importer) ──

export const DOCUMENT_TYPES = ["invoice", "purchase_order", "contract", "receipt", "custom"];

export const validateTemplateName = (name) =>
  !name?.trim() ? "Template name is required." : null;

export const validateTemplateFieldsArray = (fields) => {
  const errors = [];
  const seenKeys = new Set();
  (fields || []).forEach((f, idx) => {
    const key = (f?.field_key || "").trim();
    const label = `Field ${idx + 1}${key ? ` (${key})` : ""}`;
    if (!key) {
      errors.push(`${label}: field key is required.`);
    } else if (seenKeys.has(key.toLowerCase())) {
      errors.push(`${label}: duplicate field key.`);
    } else {
      seenKeys.add(key.toLowerCase());
    }
    if (!f?.instruction?.trim()) {
      errors.push(`${label}: extraction instruction is required.`);
    }
  });
  return errors;
};

export const buildUniversalExtractUrl = (slug) =>
  `${API_BASE}${UNIVERSAL_EXTRACT_PATH}?template=${encodeURIComponent(slug || "")}`;

export const buildCurlSnippet = (slug) => `curl -X POST "${buildUniversalExtractUrl(slug)}" \\
  -H "Authorization: Bearer YOUR_ACCESS_TOKEN" \\
  -F "file=@/path/to/document.pdf"`;

export const buildFetchSnippet = (slug) => `const formData = new FormData();
formData.append("file", file);

const response = await fetch("${buildUniversalExtractUrl(slug)}", {
  method: "POST",
  headers: {
    Authorization: "Bearer YOUR_ACCESS_TOKEN",
  },
  body: formData,
});

const data = await response.json();
console.log(data);`;

// ─── Field type meta ─────────────────────────────────────────────────────────

export const DATA_TYPE_META = {
  text:   { label: "Text",   icon: Type,     badge: "bg-blue-500/10 text-blue-500"     },
  number: { label: "Number", icon: Hash,     badge: "bg-green-600/10 text-green-600"   },
  date:   { label: "Date",   icon: Calendar, badge: "bg-yellow-600/10 text-yellow-600" },
  table:  { label: "Table",  icon: Table2,   badge: "bg-[#8B55DD]/10 text-[#8B55DD]"   },
  object: { label: "Object", icon: Braces,   badge: "bg-orange-600/10 text-orange-600" },
};

export const SCHEMA_TYPES = ["text", "number", "date", "table", "object"];

export const RETRIEVAL_OPTIONS = [
  { value: "full_text",  label: "Full Text" },
  { value: "similarity", label: "Similarity Search" },
  { value: "fusion",     label: "Hybrid Fusion" },
];

export const LLM_MODEL_OPTIONS = [
  { value: "gemini_2_5_flash_lite", label: "Gemini 2.5 Flash Lite (Lightweight & Fast)" },
  { value: "gemini_2_5_flash", label: "Gemini 2.5 Flash (Full & Accurate)" },
];

export const WIZARD_STEPS = [
  { num: 1, label: "Basics" },
  { num: 2, label: "Engine" },
  { num: 3, label: "Fields" },
];

// ─── Reusable Tailwind class bundles ─────────────────────────────────────────

export const CLS = {
  // Buttons
  btn: "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[0.8125rem] font-semibold border-none cursor-pointer transition-all duration-150 whitespace-nowrap disabled:opacity-[0.35] disabled:cursor-not-allowed",
  btnPrimary: "bg-gradient-to-r from-[#8B55DD] to-[#9177B8] text-white shadow-[0_1px_2px_rgba(0,0,0,0.1)] hover:enabled:bg-gradient-to-r hover:enabled:from-[#7A48CC] hover:enabled:to-[#8068A8] hover:enabled:shadow-[0_2px_8px_rgba(139,85,221,0.25)]",
  btnSecondary: "bg-[#F5F4F3] text-[#3F3F46] border border-black/10 hover:enabled:bg-[#EDECEB] hover:enabled:text-[#171717]",
  btnGhost: "bg-transparent text-[#71717A] border border-black/10 hover:enabled:bg-[#F5F4F3] hover:enabled:text-[#3F3F46] hover:enabled:border-black/[0.15]",
  btnSm: "!text-[0.6875rem] !px-2 !py-0.5",

  // Inputs
  input: "w-full bg-white border border-black/10 rounded-lg px-3 py-2 text-[0.8125rem] text-[#171717] outline-none transition-all duration-200 hover:border-black/20 focus:border-[rgba(187,66,174,0.36)] focus:shadow-[0_0_0_3px_rgba(139,85,221,0.08)]",
  inputError: "border-[#D31C1C] shadow-[0_0_0_3px_rgba(211,28,28,0.06)]",

  // Labels
  label: "text-[0.625rem] font-bold text-[#52525B] uppercase tracking-[0.06em] flex items-center",
  labelHint: "font-normal text-[#9CA3AF] normal-case tracking-normal text-[0.625rem] ml-1.5",

  // Form
  formGroup: "flex flex-col gap-1.5",
  errorText: "text-[0.6875rem] text-[#D31C1C] flex items-center gap-1",

  // Field type badge
  fieldTypeBadge: "text-[0.5625rem] font-bold px-2 py-[0.1875rem] rounded-2xl uppercase tracking-[0.04em] inline-flex items-center gap-1 flex-shrink-0",

  // Panel
  panelTopbar: "h-[44px] flex items-center px-3 gap-2 border-b border-[#e5e7eb] flex-shrink-0",
  panelTitle: "text-[0.6875rem] font-bold text-[#71717A] uppercase tracking-[0.06em] flex items-center gap-2",

  // Field count badge
  fieldCountBadge: "bg-[#8B55DD]/10 text-[#8B55DD] text-[0.6875rem] font-bold px-2 py-0.5 rounded-full ml-1.5",
};

// ─── Suggested Fields per Document Type ──────────────────────────────────────

export const SUGGESTED_FIELDS = {
  invoice: [
    { field_key: "vendor_name",    data_type: "text",   instruction: "Extract the company or vendor name issuing the invoice", output_rules: "" },
    { field_key: "customer_name",  data_type: "text",   instruction: "Extract the bill-to company or customer name", output_rules: "" },
    { field_key: "invoice_number", data_type: "text",   instruction: "Extract the invoice or bill number", output_rules: "" },
    { field_key: "invoice_date",   data_type: "date",   instruction: "Extract the invoice date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "due_date",       data_type: "date",   instruction: "Extract the payment due date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "total_amount",   data_type: "number", instruction: "Extract the total amount due including all taxes", output_rules: "Return as float, no currency symbols" },
    { field_key: "tax_amount",     data_type: "number", instruction: "Extract the tax or VAT amount", output_rules: "Return as float, no currency symbols" },
    { field_key: "currency",       data_type: "text",   instruction: "Extract the currency code (e.g. USD, EUR, INR)", output_rules: "" },
    { field_key: "line_items",     data_type: "table",  instruction: "Extract all line items as a table with quantity, price and description", output_rules: "Columns: description, quantity, unit_price, total" },
    { field_key: "vendor_address", data_type: "text",   instruction: "Extract the vendor full address", output_rules: "" },
  ],
  purchase_order: [
    { field_key: "po_number",      data_type: "text",   instruction: "Extract the purchase order number", output_rules: "" },
    { field_key: "vendor_name",    data_type: "text",   instruction: "Extract the vendor or supplier name", output_rules: "" },
    { field_key: "order_date",     data_type: "date",   instruction: "Extract the purchase order date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "delivery_date",  data_type: "date",   instruction: "Extract the expected delivery date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "total_value",    data_type: "number", instruction: "Extract the total order value", output_rules: "Return as float" },
    { field_key: "buyer_name",     data_type: "text",   instruction: "Extract the buyer or purchasing company name", output_rules: "" },
    { field_key: "line_items",     data_type: "table",  instruction: "Extract all order line items", output_rules: "Columns: item_code, description, quantity, unit_price, total" },
  ],
  contract: [
    { field_key: "contract_title", data_type: "text",   instruction: "Extract the title or name of the contract", output_rules: "" },
    { field_key: "effective_date", data_type: "date",   instruction: "Extract the contract start or effective date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "expiry_date",    data_type: "date",   instruction: "Extract the contract end or expiry date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "party_1",        data_type: "text",   instruction: "Extract the first party (service provider) name", output_rules: "" },
    { field_key: "party_2",        data_type: "text",   instruction: "Extract the second party (client) name", output_rules: "" },
    { field_key: "contract_value", data_type: "number", instruction: "Extract the total contract value", output_rules: "Return as float" },
    { field_key: "governing_law",  data_type: "text",   instruction: "Extract the governing law or jurisdiction", output_rules: "" },
  ],
  receipt: [
    { field_key: "merchant_name",  data_type: "text",   instruction: "Extract the store or merchant name", output_rules: "" },
    { field_key: "receipt_date",   data_type: "date",   instruction: "Extract the transaction date", output_rules: "Format as YYYY-MM-DD" },
    { field_key: "total_amount",   data_type: "number", instruction: "Extract the total amount paid", output_rules: "Return as float" },
    { field_key: "payment_method", data_type: "text",   instruction: "Extract the payment method (cash, card, UPI, etc.)", output_rules: "" },
    { field_key: "items",          data_type: "table",  instruction: "Extract all purchased items", output_rules: "Columns: name, quantity, price" },
  ],
  custom: [],
};